/**
 * Microsite Analytics - Lightweight Client-Side Tracker
 *
 * Features:
 * - Session and visitor tracking
 * - Page view and CTA click tracking
 * - Time on page measurement
 * - Referrer source tracking
 * - Event batching for performance
 * - Automatic edge function calls
 */

import { supabase } from "@/integrations/supabase/client";
import type { MicrositeEventType, TrackEventParams } from "@/types/analytics";

// ============================================================================
// Constants
// ============================================================================

const SESSION_STORAGE_KEY = 'microsite_session_id';
const VISITOR_STORAGE_KEY = 'microsite_visitor_id';
const PAGE_START_TIME_KEY = 'microsite_page_start_time';
const BATCH_INTERVAL = 5000; // Send events every 5 seconds
const MAX_BATCH_SIZE = 10; // Max events per batch

// ============================================================================
// Storage & ID Generation
// ============================================================================

/**
 * Generate a unique identifier
 */
const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Get or create session ID (expires when browser closes)
 */
export const getSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = `session_${generateId()}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    // Fallback if sessionStorage is not available
    return `session_${generateId()}`;
  }
};

/**
 * Get or create visitor ID (persists across sessions)
 */
export const getVisitorId = (): string => {
  try {
    let visitorId = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (!visitorId) {
      visitorId = `visitor_${generateId()}`;
      localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
    }
    return visitorId;
  } catch (error) {
    // Fallback if localStorage is not available
    return `visitor_${generateId()}`;
  }
};

/**
 * Track page start time for time-on-page calculation
 */
export const startPageTimer = (): void => {
  try {
    sessionStorage.setItem(PAGE_START_TIME_KEY, Date.now().toString());
  } catch (error) {
    // Silently fail if storage not available
  }
};

/**
 * Get time spent on page in seconds
 */
export const getTimeOnPage = (): number => {
  try {
    const startTime = sessionStorage.getItem(PAGE_START_TIME_KEY);
    if (startTime) {
      const elapsed = Date.now() - parseInt(startTime, 10);
      return Math.floor(elapsed / 1000); // Convert to seconds
    }
  } catch (error) {
    // Silently fail
  }
  return 0;
};

// ============================================================================
// Referrer Extraction
// ============================================================================

/**
 * Extract domain from URL
 */
const extractDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    return null;
  }
};

/**
 * Get referrer information
 */
export const getReferrerInfo = (): { url: string | null; domain: string | null } => {
  const referrerUrl = document.referrer || null;
  const referrerDomain = referrerUrl ? extractDomain(referrerUrl) : null;

  return {
    url: referrerUrl,
    domain: referrerDomain,
  };
};

// ============================================================================
// Event Category Mapping
// ============================================================================

const EVENT_CATEGORY_MAP: Record<MicrositeEventType, string> = {
  'page_view': 'navigation',
  'cta_click': 'cta',
  'phone_click': 'cta',
  'email_click': 'cta',
  'whatsapp_click': 'cta',
  'appointment_booking': 'form_submission',
  'directions_click': 'cta',
  'product_view': 'navigation',
  'social_click': 'social',
  'navigation_click': 'navigation',
  'form_submission': 'form_submission',
};

/**
 * Get event category from event type
 */
const getEventCategory = (eventType: MicrositeEventType): string => {
  return EVENT_CATEGORY_MAP[eventType] || 'other';
};

/**
 * Determine if event is a conversion
 */
const isConversionEvent = (eventType: MicrositeEventType): boolean => {
  return [
    'appointment_booking',
    'phone_click',
    'whatsapp_click',
    'email_click',
    'form_submission',
  ].includes(eventType);
};

// ============================================================================
// Event Queue & Batching
// ============================================================================

interface QueuedEvent {
  subdomain_config_id: string;
  location_id?: string;
  workspace_id: string;
  event_type: MicrositeEventType;
  event_category: string;
  page_path: string;
  page_name: string;
  session_id: string;
  visitor_id: string;
  referrer_url?: string | null;
  referrer_domain?: string | null;
  event_data?: Record<string, any>;
  user_agent: string;
  is_conversion: boolean;
  time_on_page?: number;
  created_at: string;
}

/**
 * Map page paths to human-readable display names
 */
const PAGE_DISPLAY_NAMES: Record<string, string> = {
  '/': 'Home',
  '/contact-us': 'Contact Us',
  '/appointment': 'Book Appointment',
  '/reviews': 'Reviews',
  '/posts': 'Updates',
  '/blogs': 'Blog',
  '/products-services': 'Products & Services',
  '/gallery': 'Gallery',
  '/about': 'About',
  '/services': 'Services',
};

/**
 * Get human-readable page name from path
 */
const getPageName = (path: string): string => {
  return PAGE_DISPLAY_NAMES[path] || path.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Home';
};

let eventQueue: QueuedEvent[] = [];
let batchTimer: NodeJS.Timeout | null = null;

/**
 * Send batched events to edge function
 */
const flushEventQueue = async (): Promise<void> => {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = []; // Clear queue immediately

  console.log('[Analytics] Flushing', eventsToSend.length, 'events to server');
  console.log('[Analytics] Events:', eventsToSend.map(e => ({type: e.event_type, data: e.event_data})));

  try {
    // Call Supabase edge function
    const { data, error } = await supabase.functions.invoke('track-microsite-event', {
      body: { events: eventsToSend },
    });

    if (error) {
      console.error('[Analytics] Error sending events:', error);
      // Don't re-queue failed events to avoid memory issues
    } else {
      console.log('[Analytics] Successfully sent events. Server response:', data);
    }
  } catch (error) {
    console.error('[Analytics] Failed to send events:', error);
  }
};

/**
 * Add event to queue and schedule batch send
 */
const queueEvent = (event: QueuedEvent): void => {
  console.log('[Analytics] Queuing event:', event.event_type, event.event_data);
  eventQueue.push(event);

  console.log('[Analytics] Queue size:', eventQueue.length, '/', MAX_BATCH_SIZE);

  // Send immediately if batch is full
  if (eventQueue.length >= MAX_BATCH_SIZE) {
    console.log('[Analytics] Batch full, flushing immediately');
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
    flushEventQueue();
    return;
  }

  // Otherwise, schedule batch send
  if (!batchTimer) {
    console.log('[Analytics] Scheduling batch send in', BATCH_INTERVAL, 'ms');
    batchTimer = setTimeout(() => {
      batchTimer = null;
      flushEventQueue();
    }, BATCH_INTERVAL);
  }
};

/**
 * Send events on page unload
 * Note: beforeunload is unreliable on mobile, but works well on desktop
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventQueue.length > 0) {
      flushEventQueue();
    }
  });

  // Also track on visibility change (more reliable for mobile/tab switching)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && eventQueue.length > 0) {
      flushEventQueue();
    }
  });
}

// ============================================================================
// Main Tracking Function
// ============================================================================

/**
 * Track a microsite analytics event
 */
export const trackMicrositeEvent = async (params: TrackEventParams): Promise<void> => {
  try {
    const {
      subdomainConfigId,
      locationId,
      workspaceId,
      eventType,
      eventData = {},
      isConversion,
      timeOnPage,
    } = params;

    // Validate required parameters
    if (!subdomainConfigId || !workspaceId) {
      console.warn('[Analytics] Skipping event - missing required parameters');
      return;
    }

    // Get session and visitor IDs
    const sessionId = getSessionId();
    const visitorId = getVisitorId();

    // Get referrer info
    const referrer = getReferrerInfo();

    // For page_view and conversion events, automatically capture time on page if not provided
    const shouldIncludeTime = eventType === 'page_view' || isConversionEvent(eventType);
    const finalTimeOnPage = timeOnPage ?? (shouldIncludeTime ? getTimeOnPage() : undefined);

    // Build event object
    const pagePath = window.location.pathname;
    const event: QueuedEvent = {
      subdomain_config_id: subdomainConfigId,
      location_id: locationId,
      workspace_id: workspaceId,
      event_type: eventType,
      event_category: getEventCategory(eventType),
      page_path: pagePath,
      page_name: getPageName(pagePath),
      session_id: sessionId,
      visitor_id: visitorId,
      referrer_url: referrer.url,
      referrer_domain: referrer.domain,
      event_data: eventData,
      user_agent: navigator.userAgent,
      is_conversion: isConversion ?? isConversionEvent(eventType),
      time_on_page: finalTimeOnPage,
      created_at: new Date().toISOString(),
    };

    // Add to queue
    queueEvent(event);
  } catch (error) {
    // Fail silently to not disrupt user experience
    console.error('[Analytics] Tracking error:', error);
  }
};

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Track page view
 */
export const trackPageView = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string
): void => {
  startPageTimer(); // Start timer for this page

  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'page_view',
  });
};

/**
 * Track phone click
 */
export const trackPhoneClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  phoneNumber: string
): void => {
  console.log('[Analytics] Tracking phone click:', phoneNumber);
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'phone_click',
    eventData: { phone_number: phoneNumber },
    isConversion: true,
  });
};

/**
 * Track email click
 */
export const trackEmailClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  email: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'email_click',
    eventData: { email },
    isConversion: true,
  });
};

/**
 * Track WhatsApp click
 */
export const trackWhatsAppClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string
): void => {
  console.log('[Analytics] Tracking WhatsApp click');
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'whatsapp_click',
    isConversion: true,
  });
};

/**
 * Track directions click
 */
export const trackDirectionsClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  address: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'directions_click',
    eventData: { address },
  });
};

/**
 * Track appointment booking (conversion)
 */
export const trackAppointmentBooking = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  appointmentData?: Record<string, any>
): void => {
  console.log('[Analytics] Tracking appointment booking:', appointmentData);
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'appointment_booking',
    eventData: appointmentData,
    isConversion: true,
  });
};

/**
 * Track social media click
 */
export const trackSocialClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  platform: string,
  url: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'social_click',
    eventData: { platform, url },
  });
};

/**
 * Track product view
 */
export const trackProductView = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  productId: string,
  productName?: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'product_view',
    eventData: { product_id: productId, product_name: productName },
  });
};

/**
 * Track navigation click
 */
export const trackNavigationClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  destination: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'navigation_click',
    eventData: { destination },
  });
};

/**
 * Track generic CTA click
 */
export const trackCTAClick = (
  subdomainConfigId: string,
  locationId: string | undefined,
  workspaceId: string,
  ctaLabel: string,
  ctaType?: string
): void => {
  trackMicrositeEvent({
    subdomainConfigId,
    locationId,
    workspaceId,
    eventType: 'cta_click',
    eventData: { label: ctaLabel, type: ctaType },
  });
};
